import { NAMED_PLACEHOLDER_PATTERN } from './parseTemplateSegments';

// Customer-controlled text → node tree, never HTML (injection must stay unrepresentable).
export const WHATSAPP_FORMAT_STYLES = {
    BOLD: 'bold',
    INLINE_CODE: 'inlineCode',
    ITALIC: 'italic',
    MONOSPACE: 'monospace',
    STRIKETHROUGH: 'strikethrough',
} as const;

export type WhatsAppFormatStyle = (typeof WHATSAPP_FORMAT_STYLES)[keyof typeof WHATSAPP_FORMAT_STYLES];

export type WhatsAppFormattedNode =
    | { type: 'text'; value: string }
    | { type: 'style'; style: WhatsAppFormatStyle; children: WhatsAppFormattedNode[] }
    | { type: 'quote'; children: WhatsAppFormattedNode[] }
    | { type: 'bulletList'; items: WhatsAppFormattedNode[][] }
    | { type: 'numberedList'; items: { number: string; children: WhatsAppFormattedNode[] }[] };

const INLINE_MARKERS: Record<string, WhatsAppFormatStyle> = {
    '*': WHATSAPP_FORMAT_STYLES.BOLD,
    _: WHATSAPP_FORMAT_STYLES.ITALIC,
    '~': WHATSAPP_FORMAT_STYLES.STRIKETHROUGH,
};

const MONOSPACE_FENCE = '```';
const INLINE_CODE_MARKER = '`';

// Anchored, non-global copy of the template grammar so the two definitions cannot drift.
const PLACEHOLDER_AT_START = new RegExp(`^${NAMED_PLACEHOLDER_PATTERN.source}`);

// A URL's `_`/`*`/`~` belong to the address — formatting it would corrupt what the reader copies.
const URL_AT_START = /^(?:https?:\/\/|www\.)[^\s`]*[^\s`.,;:!?'"()[\]{}<>*_~]/i;

const QUOTE_PREFIX = /^> /;
const BULLET_PREFIX = /^[*-] /;
const NUMBERED_PREFIX = /^(\d+)\. (.*)$/;

// NUL is stripped from the input, so a sentinel can never collide with a template's own content.
const SENTINEL_DELIMITER = '\u0000';
const sentinelFor = (index: number) => `${SENTINEL_DELIMITER}${index}${SENTINEL_DELIMITER}`;

const WHITESPACE = /\s/;

// WhatsApp only formats a non-empty run that doesn't touch whitespace — `* x *` stays literal.
// Boundary chars, not a slice: slicing per candidate made a 4k-char message take over a second.
const isFormattableRange = (text: string, start: number, end: number): boolean =>
    end > start && !WHITESPACE.test(text[start] as string) && !WHITESPACE.test(text[end - 1] as string);

const findClosingMarker = (text: string, marker: string, openIndex: number): number => {
    for (let i = openIndex + 1; i < text.length; i += 1) {
        if (text[i] === marker && isFormattableRange(text, openIndex + 1, i)) return i;
    }
    return -1;
};

// Shared by scanStep and maskFences so a fence match can never drift between the two passes.
const matchFence = (text: string, index: number): { end: number; inner: string } | null => {
    if (!text.startsWith(MONOSPACE_FENCE, index)) return null;
    const close = text.indexOf(MONOSPACE_FENCE, index + MONOSPACE_FENCE.length);
    if (close === -1) return null;
    const inner = text.slice(index + MONOSPACE_FENCE.length, close);
    if (inner.length === 0) return null;
    return { end: close + MONOSPACE_FENCE.length, inner };
};

// Fences can span lines, so a pasted block's `* `/`> `/`1. `-looking lines must be masked before line grouping.
const maskFences = (text: string): { masked: string; restore: Map<string, string> } => {
    const restore = new Map<string, string>();
    let masked = '';
    let index = 0;
    let fenceIndex = 0;

    while (index < text.length) {
        const match = matchFence(text, index);
        if (match) {
            const sentinel = sentinelFor(fenceIndex);
            fenceIndex += 1;
            restore.set(sentinel, text.slice(index, match.end));
            masked += sentinel;
            index = match.end;
        } else {
            masked += text[index];
            index += 1;
        }
    }

    return { masked, restore };
};

const restoreSentinels = (text: string, restore: Map<string, string>): string => {
    let result = text;
    restore.forEach((original, sentinel) => {
        result = result.split(sentinel).join(original);
    });
    return result;
};

type ScanStep = { consumed: number; literal: string } | { consumed: number; node: WhatsAppFormattedNode };

// parseInner is passed in so the mutual recursion for nesting (`*_both_*`) is not a forward reference.
const scanStep = (text: string, index: number, parseInner: (inner: string) => WhatsAppFormattedNode[]): ScanStep => {
    const rest = text.slice(index);

    // Unsubstituted `{{agent_first_name}}` in a preview would otherwise be italicised by its underscores.
    const placeholder = PLACEHOLDER_AT_START.exec(rest);
    if (placeholder) {
        const matched = placeholder[0] as string;
        return { consumed: matched.length, literal: matched };
    }

    // Consumed whole as a literal so the markers inside it survive into the text node.
    const url = URL_AT_START.exec(rest);
    if (url) {
        const matched = url[0] as string;
        return { consumed: matched.length, literal: matched };
    }

    if (text.startsWith(MONOSPACE_FENCE, index)) {
        const fence = matchFence(text, index);
        if (fence) {
            return {
                consumed: fence.end - index,
                node: {
                    children: [{ type: 'text', value: fence.inner }],
                    style: WHATSAPP_FORMAT_STYLES.MONOSPACE,
                    type: 'style',
                },
            };
        }

        // Without this, the inline-code branch below would pair backtick 1 with backtick 3.
        return { consumed: MONOSPACE_FENCE.length, literal: MONOSPACE_FENCE };
    }

    // Not added to INLINE_MARKERS: that path recurses through parseInner, which would format inside the code.
    if (text[index] === INLINE_CODE_MARKER) {
        const closeIndex = findClosingMarker(text, INLINE_CODE_MARKER, index);
        if (closeIndex !== -1) {
            return {
                consumed: closeIndex + 1 - index,
                node: {
                    children: [{ type: 'text', value: text.slice(index + 1, closeIndex) }],
                    style: WHATSAPP_FORMAT_STYLES.INLINE_CODE,
                    type: 'style',
                },
            };
        }
    }

    const char = text[index] as string;
    const style = INLINE_MARKERS[char];
    if (style) {
        const closeIndex = findClosingMarker(text, char, index);
        if (closeIndex !== -1) {
            return {
                consumed: closeIndex + 1 - index,
                node: { children: parseInner(text.slice(index + 1, closeIndex)), style, type: 'style' },
            };
        }
    }

    return { consumed: 1, literal: char };
};

// The inline scanner: bold/italic/strikethrough/monospace/inline-code, no line/block awareness.
export function parseInline(text: string): WhatsAppFormattedNode[] {
    const nodes: WhatsAppFormattedNode[] = [];
    let literal = '';

    const flushLiteral = () => {
        if (literal) nodes.push({ type: 'text', value: literal });
        literal = '';
    };

    let index = 0;
    while (index < text.length) {
        const step = scanStep(text, index, parseInline);
        if ('literal' in step) {
            literal += step.literal;
        } else {
            flushLiteral();
            nodes.push(step.node);
        }
        index += step.consumed;
    }

    flushLiteral();
    return nodes;
}

type LineGroup =
    | { type: 'plain'; lines: string[] }
    | { type: 'quote'; lines: string[] }
    | { type: 'bulletList'; items: string[] }
    | { type: 'numberedList'; items: { number: string; body: string }[] };

const groupLines = (lines: string[]): LineGroup[] =>
    lines.reduce<LineGroup[]>((groups, line) => {
        const last = groups[groups.length - 1];

        if (QUOTE_PREFIX.test(line)) {
            const body = line.slice(2);
            if (last?.type === 'quote') last.lines.push(body);
            else groups.push({ lines: [body], type: 'quote' });
            return groups;
        }

        if (BULLET_PREFIX.test(line)) {
            const body = line.slice(2);
            if (last?.type === 'bulletList') last.items.push(body);
            else groups.push({ items: [body], type: 'bulletList' });
            return groups;
        }

        const numbered = NUMBERED_PREFIX.exec(line);
        if (numbered) {
            const item = { body: numbered[2] as string, number: numbered[1] as string };
            if (last?.type === 'numberedList') last.items.push(item);
            else groups.push({ items: [item], type: 'numberedList' });
            return groups;
        }

        if (last?.type === 'plain') last.lines.push(line);
        else groups.push({ lines: [line], type: 'plain' });
        return groups;
    }, []);

export function parseWhatsAppFormatting(text: string): WhatsAppFormattedNode[] {
    // A `\r` would survive into text nodes, break NUMBERED_PREFIX's `$` anchor and hide blank lines.
    const normalized = text.replace(/\r\n?/g, '\n');
    // Sentinels are NUL-delimited, so a NUL in customer text could make restore rewrite real content.
    const { masked, restore } = maskFences(normalized.split(SENTINEL_DELIMITER).join(''));
    const groups = groupLines(masked.split('\n'));

    return groups.flatMap((group): WhatsAppFormattedNode[] => {
        if (group.type === 'plain') {
            // One newline per blank line — groupLines drops the one separating this group from the next block.
            const isBlankOnly = group.lines.every((line) => line.length === 0);
            if (isBlankOnly && groups.length > 1) return [{ type: 'text', value: '\n'.repeat(group.lines.length) }];
            return parseInline(restoreSentinels(group.lines.join('\n'), restore));
        }

        if (group.type === 'quote') {
            return [
                {
                    children: parseInline(restoreSentinels(group.lines.join('\n'), restore)),
                    type: 'quote',
                },
            ];
        }

        if (group.type === 'bulletList') {
            return [
                {
                    items: group.items.map((item) => parseInline(restoreSentinels(item, restore))),
                    type: 'bulletList',
                },
            ];
        }

        return [
            {
                items: group.items.map(({ body, number }) => ({
                    children: parseInline(restoreSentinels(body, restore)),
                    number,
                })),
                type: 'numberedList',
            },
        ];
    });
}
